import { toValue, getCurrentScope, onScopeDispose } from 'vue'
import { useActivityTracking } from '../useActivityTracking.js'

/**
 * Recherche d'une page liste : on attend que l'utilisateur ait fini de taper
 * avant d'interroger le serveur.
 *
 * Le composable d'origine mélangeait deux choses qui n'ont rien à voir : le
 * report du chargement (une question de réseau) et le surlignage des résultats
 * (une question d'affichage). Une liste sans surlignage devait donc quand même
 * embarquer mark.js, et une liste qui voulait surligner héritait du debounce.
 * Les deux sont séparés : ici le debounce et le tracking, dans
 * `useSearchHighlight` le surlignage.
 *
 * `cancel()` existe pour la réinitialisation des filtres : sans lui, le
 * chargement en attente retombe après coup et réapplique la recherche qu'on
 * vient d'effacer.
 *
 * @param {*} searchRef - ref, getter ou valeur du terme recherché
 * @param {() => any} load - rechargement de la liste (page 1)
 * @param {{ delay?: number, track?: string, onLoaded?: () => void }} [options]
 *   track = contexte du panneau d'activité (« quotes », « formateurs »…)
 * @returns {{ handleSearchChange: (value?: string) => void, cancel: () => void }}
 */
export function useDebouncedListSearch(searchRef, load, options = {}) {
  const { delay = 300, track = null, onLoaded = null } = options
  const { trackSearch } = useActivityTracking()

  let searchTimeout = null

  const cancel = () => {
    if (searchTimeout) {
      clearTimeout(searchTimeout)
      searchTimeout = null
    }
  }

  const handleSearchChange = (value) => {
    const term = value === undefined ? toValue(searchRef) : value
    if (track) trackSearch(term, track)
    cancel()
    searchTimeout = setTimeout(() => {
      searchTimeout = null
      const result = load()
      if (onLoaded) Promise.resolve(result).then(() => onLoaded()).catch(() => {})
    }, delay)
  }

  // Même raison que dans useDebouncedSearch : une recherche en attente ne doit
  // pas survivre à la page qui l'a lancée.
  if (getCurrentScope()) onScopeDispose(cancel)

  return { handleSearchChange, cancel }
}
